import type {
  CatalogMetric,
  CatalogTable,
  GetTableDetailsResponse,
  TableColumn,
} from "./dataModelCatalog.types";

const SNOWFLAKE_LOGO = "/logos/snowflake.svg";
const TABLEAU_LOGO = "/logos/tableau.svg";

const TABLE_SOURCE: Record<string, { id: string; name: string; logoUrl: string }> =
  {
    orders: { id: "src_snowflake", name: "Snowflake", logoUrl: SNOWFLAKE_LOGO },
    promotion: { id: "src_tableau", name: "Tableau", logoUrl: TABLEAU_LOGO },
    warehouses: { id: "src_snowflake", name: "Snowflake", logoUrl: SNOWFLAKE_LOGO },
    products: { id: "src_snowflake", name: "Snowflake", logoUrl: SNOWFLAKE_LOGO },
    regions: { id: "src_tableau", name: "Tableau", logoUrl: TABLEAU_LOGO },
    budget: { id: "src_tableau", name: "Tableau", logoUrl: TABLEAU_LOGO },
    shipments: { id: "src_snowflake", name: "Snowflake", logoUrl: SNOWFLAKE_LOGO },
    customers: { id: "src_snowflake", name: "Snowflake", logoUrl: SNOWFLAKE_LOGO },
  };

const TABLE_TYPES: Record<string, "Fact" | "Dim"> = {
  orders: "Fact",
  promotion: "Fact",
  warehouses: "Dim",
  products: "Dim",
  regions: "Dim",
  budget: "Fact",
  shipments: "Fact",
  customers: "Dim",
};

export const SEED_CATALOG_TABLES: CatalogTable[] = [
  "orders",
  "promotion",
  "warehouses",
  "products",
  "regions",
  "budget",
  "shipments",
  "customers",
  "suppliers",
  "returns",
  "payments",
  "invoices",
  "stores",
  "categories",
  "brands",
  "campaigns",
  "sessions",
  "pageviews",
  "carts",
  "refunds",
].map((id) => ({
  id,
  name: id.charAt(0).toUpperCase() + id.slice(1),
  tableType: TABLE_TYPES[id] ?? "Fact",
  source: TABLE_SOURCE[id] ?? TABLE_SOURCE.orders,
}));

export const SEED_CATALOG_METRICS: CatalogMetric[] = [
  {
    id: "gross-sales",
    name: "Gross sales",
    isAutoGenerated: true,
    sourceTableIds: ["orders"],
  },
  {
    id: "order-count",
    name: "Order count",
    isAutoGenerated: false,
    sourceTableIds: [],
  },
  {
    id: "conversion-rate",
    name: "Conversion rate",
    isAutoGenerated: false,
    sourceTableIds: [],
  },
  {
    id: "units-sold",
    name: "Units sold",
    isAutoGenerated: true,
    sourceTableIds: ["products"],
  },
  {
    id: "avg-rating",
    name: "Avg rating",
    isAutoGenerated: false,
    sourceTableIds: [],
  },
  {
    id: "cart-size",
    name: "Cart size",
    isAutoGenerated: false,
    sourceTableIds: [],
  },
  {
    id: "avg-order-value",
    name: "Avg order value",
    isAutoGenerated: false,
    sourceTableIds: [],
  },
  {
    id: "gross-margin",
    name: "Gross margin",
    isAutoGenerated: true,
    sourceTableIds: ["promotion"],
  },
  {
    id: "net-revenue",
    name: "Net revenue",
    isAutoGenerated: true,
    sourceTableIds: ["orders", "refunds"],
  },
  {
    id: "return-rate",
    name: "Return rate",
    isAutoGenerated: true,
    sourceTableIds: ["returns"],
  },
  {
    id: "payment-success-rate",
    name: "Payment success rate",
    isAutoGenerated: true,
    sourceTableIds: ["payments"],
  },
  {
    id: "invoice-collection-rate",
    name: "Invoice collection rate",
    isAutoGenerated: true,
    sourceTableIds: ["invoices"],
  },
  {
    id: "store-footfall",
    name: "Store footfall",
    isAutoGenerated: false,
    sourceTableIds: [],
  },
  {
    id: "active-campaigns",
    name: "Active campaigns",
    isAutoGenerated: false,
    sourceTableIds: [],
  },
  {
    id: "average-basket-size",
    name: "Average basket size",
    isAutoGenerated: false,
    sourceTableIds: [],
  },
  {
    id: "repeat-purchase-rate",
    name: "Repeat purchase rate",
    isAutoGenerated: false,
    sourceTableIds: [],
  },
  {
    id: "session-conversion",
    name: "Session conversion",
    isAutoGenerated: true,
    sourceTableIds: ["sessions", "pageviews", "carts"],
  },
  {
    id: "cart-abandonment-rate",
    name: "Cart abandonment rate",
    isAutoGenerated: true,
    sourceTableIds: ["carts"],
  },
  {
    id: "supplier-fill-rate",
    name: "Supplier fill rate",
    isAutoGenerated: false,
    sourceTableIds: [],
  },
  {
    id: "inventory-turnover",
    name: "Inventory turnover",
    isAutoGenerated: false,
    sourceTableIds: [],
  },
];

type MockField = { name: string; keyType?: "PK" | "FK" };

const MOCK_FIELDS: Record<string, MockField[]> = {
  orders: [
    { name: "order_id", keyType: "PK" },
    { name: "customer_id", keyType: "FK" },
    { name: "order_date" },
    { name: "status" },
    { name: "total_amount" },
  ],
  promotion: [
    { name: "promo_id", keyType: "PK" },
    { name: "name" },
    { name: "discount_pct" },
  ],
  warehouses: [
    { name: "warehouse_id", keyType: "PK" },
    { name: "region_id", keyType: "FK" },
    { name: "name" },
  ],
  products: [
    { name: "product_id", keyType: "PK" },
    { name: "name" },
    { name: "category" },
  ],
  regions: [{ name: "region_id", keyType: "PK" }, { name: "name" }],
  budget: [
    { name: "budget_id", keyType: "PK" },
    { name: "fiscal_year" },
    { name: "amount" },
  ],
  shipments: [
    { name: "shipment_id", keyType: "PK" },
    { name: "order_id", keyType: "FK" },
    { name: "shipped_at" },
  ],
  customers: [
    { name: "customer_id", keyType: "PK" },
    { name: "name" },
    { name: "email" },
  ],
};

const FK_COLUMN_TO_TABLE: Record<string, string> = {
  customer_id: "customers",
  order_id: "orders",
  region_id: "regions",
};

const toColumn = (tableId: string, field: MockField, index: number): TableColumn => ({
  id: `col_${tableId}_${index}`,
  name: field.name,
  dataType: field.keyType === "PK" ? "INTEGER" : "VARCHAR",
  isPrimaryKey: field.keyType === "PK",
  isForeignKey: field.keyType === "FK",
  nullable: field.keyType !== "PK",
});

export const buildTableDetailsResponse = (
  tableId: string,
): GetTableDetailsResponse | null => {
  const fields = MOCK_FIELDS[tableId];
  const catalogTable = SEED_CATALOG_TABLES.find((t) => t.id === tableId);
  if (!fields || !catalogTable) return null;

  const columns = fields.map((field, index) => toColumn(tableId, field, index));

  const foreignKeyHints = fields
    .filter((f) => f.keyType === "FK")
    .map((f) => {
      const targetTableId = FK_COLUMN_TO_TABLE[f.name];
      if (!targetTableId) return null;
      const targetPk = MOCK_FIELDS[targetTableId]?.find((t) => t.keyType === "PK");
      if (!targetPk) return null;
      const sourceColumnId = columns.find((c) => c.name === f.name)?.id;
      const targetColumnId = `col_${targetTableId}_0`;
      if (!sourceColumnId) return null;
      return {
        sourceColumnId,
        targetTableId,
        targetColumnId,
        confidence: 0.95,
      };
    })
    .filter((h): h is NonNullable<typeof h> => h != null);

  return {
    data: {
      id: tableId,
      name: catalogTable.name,
      description: `${catalogTable.name} table`,
      columns,
      foreignKeyHints,
    },
  };
};

/** FK column name suffix → referenced table id (for connected tile highlighting). */
export const getConnectedTableIdsFromSchema = (
  tableId: string,
): ReadonlySet<string> => {
  const fields = MOCK_FIELDS[tableId] ?? [];
  const related = new Set<string>();
  for (const field of fields) {
    if (field.keyType !== "FK") continue;
    const target = FK_COLUMN_TO_TABLE[field.name];
    if (target) related.add(target);
  }
  return related;
};

/** Canvas node field rows (name + keyType). */
export const getTableFieldRows = (
  tableId: string,
): { name: string; keyType?: "PK" | "FK" }[] => MOCK_FIELDS[tableId] ?? [];
